import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import PageLayout from "../../layouts/PageLayout";

const apiUrl = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
});

const unwrapShipments = (response) => {
    const data = response?.data?.data ?? response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const imageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const baseUrl = process.env.REACT_APP_IMG_URL || "";
    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const statusVariant = (status) => {
    if (status === "Recieved by Admin" || status === "Received by Admin") return "success";
    if (status === "Shipped" || status === "Dispatched") return "primary";
    return "secondary";
};

export default function StoreSellerMyShipmentsPage() {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadShipments = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(`${apiUrl}/store-seller/shipments`, {
                headers: authHeaders(),
            });
            setShipments(unwrapShipments(response));
        } catch (requestError) {
            console.error("Could not load seller shipment history:", requestError);
            setError(requestError.response?.data?.message || "Could not load your shipments.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadShipments();
    }, [loadShipments]);

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">My Shipments</h3>
                        <p className="text-muted mb-0">Review dispatch details and receipt status for your shipments.</p>
                    </div>
                    <Button as={Link} to="/seller-store-shipments/create">Create shipment</Button>
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    {error}
                    <Button variant="link" className="p-0 ms-2 align-baseline" onClick={loadShipments}>Retry</Button>
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : shipments.length === 0 ? (
                        <div className="p-4">
                            <p className="text-muted mb-3">You have not created any shipments yet.</p>
                            <Button as={Link} to="/seller-store-shipments/create">Create your first shipment</Button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover responsive className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Shipment</th>
                                        <th>Courier</th>
                                        <th>Tracking number</th>
                                        <th>Status</th>
                                        <th>Products</th>
                                        <th>Dispatched</th>
                                        <th>Received</th>
                                        <th>Proof</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shipments.map((shipment) => {
                                        const status = shipment.status || "Pending";
                                        const items = Array.isArray(shipment.items) ? shipment.items : [];

                                        return (
                                            <tr key={shipment.id}>
                                                <td>#{shipment.id}</td>
                                                <td>{shipment.courier_company?.company_name || "—"}</td>
                                                <td>{shipment.tracking_number || "—"}</td>
                                                <td><Badge bg={statusVariant(status)}>{status}</Badge></td>
                                                <td>
                                                    {items.length ? items.map((item) => (
                                                        <div key={item.id}>
                                                            {item.product?.title || item.product?.product_code || `Product #${item.product?.id || item.product_id}`}
                                                        </div>
                                                    )) : "—"}
                                                </td>
                                                <td>{formatDate(shipment.dispatched_at)}</td>
                                                <td>{formatDate(shipment.received_at)}</td>
                                                <td className="text-nowrap">
                                                    {shipment.dispatched_image && (
                                                        <a href={imageUrl(shipment.dispatched_image)} target="_blank" rel="noreferrer">Dispatch</a>
                                                    )}
                                                    {shipment.dispatched_image && shipment.received_image && " · "}
                                                    {shipment.received_image && (
                                                        <a href={imageUrl(shipment.received_image)} target="_blank" rel="noreferrer">Receipt</a>
                                                    )}
                                                    {!shipment.dispatched_image && !shipment.received_image && "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </PageLayout>
    );
}